import { motion } from "framer-motion";
import { Shield, TrendingDown, Clock, AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";

interface StatItem {
  icon: typeof Shield;
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
  isLive?: boolean;
  color?: string;
}

export const AnimatedHeroStats = () => {
  const [liveCount, setLiveCount] = useState(67);
  const [isPulsing, setIsPulsing] = useState(false);
  const [displayedValues, setDisplayedValues] = useState<Record<number, number>>({
    0: 0,
    1: 0,
    2: 0,
  });

  // Live counter auto-increment
  useEffect(() => {
    const randomInterval = () => {
      const delay = Math.random() * 60000 + 30000; // 30-90 seconds
      setTimeout(() => {
        setLiveCount((prev) => prev + 1);
        setIsPulsing(true);
        setTimeout(() => setIsPulsing(false), 500);
        randomInterval();
      }, delay);
    };
    randomInterval();
  }, []);

  // Count-up animation for static stats
  useEffect(() => {
    const targets = [2847, 4.2, 2];
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    targets.forEach((target, index) => {
      let currentStep = 0;
      const increment = target / steps;
      
      const timer = setInterval(() => {
        currentStep++;
        if (currentStep <= steps) {
          setDisplayedValues((prev) => ({
            ...prev,
            [index]: Math.min(increment * currentStep, target),
          }));
        } else {
          clearInterval(timer);
          setDisplayedValues((prev) => ({
            ...prev,
            [index]: target,
          }));
        }
      }, stepDuration);

      return () => clearInterval(timer);
    });
  }, []);

  const stats: StatItem[] = [
    {
      icon: Shield,
      value: displayedValues[0] || 0,
      suffix: "+",
      label: "Geschützte Praxen",
      color: "text-accent",
    },
    {
      icon: TrendingDown,
      value: displayedValues[1] || 0,
      prefix: "€ ",
      suffix: " Mio.",
      label: "Verhinderte Schäden",
      color: "text-accent",
    },
    {
      icon: Clock,
      value: displayedValues[2] || 0,
      prefix: "< ",
      suffix: " Std.",
      label: "Reaktionszeit Notfall",
      color: "text-primary",
    },
    {
      icon: AlertTriangle,
      value: liveCount,
      label: "Angriffe HEUTE",
      isLive: true,
      color: "text-destructive",
    },
  ];

  return (
    <div className="w-full max-w-5xl mx-auto py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6"
      >
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          const displayValue = stat.isLive 
            ? stat.value 
            : index === 1 
              ? stat.value.toFixed(1)
              : Math.floor(stat.value);

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1, duration: 0.4 }}
            >
              <Card
                className={`relative overflow-hidden transition-all duration-300 ${
                  stat.isLive && isPulsing
                    ? "shadow-[0_0_20px_rgba(239,68,68,0.4)] border-destructive/50"
                    : "hover:shadow-lg hover:border-accent/50"
                }`}
              >
                <CardContent className="pt-6 pb-6 text-center">
                  <motion.div
                    animate={stat.isLive && isPulsing ? { scale: [1, 1.2, 1] } : {}}
                    transition={{ duration: 0.5 }}
                  >
                    <Icon
                      className={`w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-3 ${
                        stat.color || "text-foreground"
                      } ${stat.isLive ? "animate-pulse" : ""}`}
                    />
                  </motion.div>

                  <div
                    className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 ${
                      stat.color || "text-foreground"
                    }`}
                  >
                    {stat.prefix}
                    {displayValue}
                    {stat.suffix}
                  </div>

                  <div className="text-xs sm:text-sm text-muted-foreground font-medium">
                    {stat.label}
                  </div>

                  {stat.isLive && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="absolute top-2 right-2"
                    >
                      <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-destructive"></span>
                      </span>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};
